import { React, useCallback, useRef, useEffect, useMemo } from "react";
import PropTypes from 'prop-types';
import '@sendbird/uikit-react/dist/index.css';
import GroupChannel from '@sendbird/uikit-react/GroupChannel';
import { sendMessage } from "../services/ParentUserService";
import { useTranslation } from "react-i18next";

// Constants
const CHAT_STATUS = {
    AVAILABLE: 1,
    UNAVAILABLE_CHECKED_OUT: 2,
    UNAVAILABLE_RESERVATION_CANCELED: 3
};

const CHAT_ISSUES_URL = 'https://share-eu1.hsforms.com/10SGQoe-EThuqmSHuIzA4ewf2ger';
const SCROLL_DELAY_MS = 500;

const bubbleStyle = {
    display: 'flex',
    alignItems: 'center',
    backgroundColor: '#F2F5FD',
    padding: '1rem',
    borderRadius: '24px',
    marginTop: '16px',
    marginLeft: '24px',
};

const bubbleStyleBlocked = {
    display: 'flex',
    alignItems: 'center',
    backgroundColor: '#FFF8ED',
    padding: '1rem',
    borderRadius: '12px',
    marginTop: '16px',
    width: 'fit-content'
};

const iconStyle = {
    marginRight: '12px',
    marginTop: '2px',
    height: '16px',
};

const textContainerStyle = {
    lineHeight: '1.4',
    fontFamily: 'Inter',
    fontSize: '14px'
};

const boldTextStyle = {
    fontWeight: 'bold',
    fontFamily: 'Inter',
    fontSize: '14px'
};

const customGroupChannelBase = {
    overflow: 'hidden',
    height: '100%',
    display: 'grid',
    gridTemplateRows: '64px 104px 1fr',
};

const customGroupChannelWithoutHeader = {
    ...customGroupChannelBase,
    gridTemplateRows: '130px 1fr',
};

const CustomGroupChannel = ({channelUrl, userNickname, chatStatus, showHeader, chatBlockedByModeration = false }) => {
    const { t } = useTranslation();
    const messageContainerRef = useRef(null);

    const customGroupChannel = useMemo(() => 
        showHeader ? customGroupChannelBase : customGroupChannelWithoutHeader,
        [showHeader]
    );

    const onClickReadMore = useCallback(() => {
        sendMessage('chatReadMore');
    }, []);

    const getMessageInputRenderer = useCallback((status) => {
        if (chatBlockedByModeration) {
            return () => (
                <div style={bubbleStyleBlocked}>
                    <div style={iconStyle}>
                        <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path d="M8 1.75C4.68629 1.75 2 4.43629 2 7.75C2 11.0637 4.68629 13.75 8 13.75C11.3137 13.75 14 11.0637 14 7.75C14 4.43629 11.3137 1.75 8 1.75Z" fill="#FFA500" stroke="#FFA500" strokeWidth="1.5"/>
                            <path d="M8 4.75V8.75" stroke="white" strokeWidth="1.5" strokeLinecap="round"/>
                            <circle cx="8" cy="11" r="0.5" fill="white"/>
                        </svg>
                    </div>
                    <div style={textContainerStyle}>
                        <div style={boldTextStyle}>{t('chat_blocked')}</div>
                        <div>{t('moderation_team_restricted_access')}</div>
                    </div>
                </div>
            );
        }

        if (status.status === CHAT_STATUS.AVAILABLE) {
            return undefined;
        }

        if (status.status === CHAT_STATUS.UNAVAILABLE_CHECKED_OUT || status.status === CHAT_STATUS.UNAVAILABLE_RESERVATION_CANCELED) {
            return () => (
                <div style={bubbleStyle}>
                    <div style={iconStyle}>
                        <svg width="16" height="16" viewBox="0 0 16 16" fill="none" role="img" aria-label="Info icon">
                            <title>Info</title>
                            <path fillRule="evenodd" clipRule="evenodd" d="M8 1.75C4.54822 1.75 1.75 4.54822 1.75 8C1.75 11.4518 4.54822 14.25 8 14.25C11.4518 14.25 14.25 11.4518 14.25 8C14.25 4.54822 11.4518 1.75 8 1.75ZM0.25 8C0.25 3.71979 3.71979 0.25 8 0.25C12.2802 0.25 15.75 3.71979 15.75 8C15.75 12.2802 12.2802 15.75 8 15.75C3.71979 15.75 0.25 12.2802 0.25 8ZM8 4C8.41421 4 8.75 4.33579 8.75 4.75V5.25C8.75 5.66421 8.41421 6 8 6C7.58579 6 7.25 5.66421 7.25 5.25V4.75C7.25 4.33579 7.58579 4 8 4ZM8 7C8.41421 7 8.75 7.33579 8.75 7.75V11.25C8.75 11.6642 8.41421 12 8 12C7.58579 12 7.25 11.6642 7.25 11.25V7.75C7.25 7.33579 7.58579 7 8 7Z" fill="#2767E7"/>
                        </svg>
                    </div>
                    <div style={textContainerStyle}>
                        <div style={boldTextStyle}>{t('chat_unavailable')}</div>
                        <div>{status.message}</div>
                    </div>
                </div>
            );
        }

        return undefined;
    }, [chatBlockedByModeration, t]);

    const CustomChannelHeader = useCallback(() => {
        if (!showHeader) {
            return null;
        }

        return (
            <div id="channel-header-fixed">
                <div id="custom-channel-header"
                    style={{
                        fontSize: '18px',
                        color: '#121417',
                        padding: '16px 24px 10px 24px',
                        fontWeight: '700',
                        borderBottom: '1px solid #DDDFE4',
                        alignItems: 'center',
                        height: '30px',
                    }}>
                    {userNickname}
                </div>
            </div>
        );
    }, [showHeader, userNickname]);

    useEffect(() => {
        // Find and store reference to message container after mount
        const messageContainer = document.querySelector('.sendbird-conversation__messages-padding');
        if (messageContainer) {
            messageContainerRef.current = messageContainer;
        }
    }, []);

    const handleAfterSendUserMessage = useCallback(() => {
        setTimeout(() => {
            if (messageContainerRef.current) {
                messageContainerRef.current.scrollTop = messageContainerRef.current.scrollHeight;
            }
        }, SCROLL_DELAY_MS);
    }, []);

    return (
        <div id="custom-group-channel" style={customGroupChannel}>
            <CustomChannelHeader />
            <div id="chat-header-report">
                    <div id="report-content">
                        <div id="icon-column">
                            <svg width="40" height="40" viewBox="0 0 40 40" fill="none" xmlns="http://www.w3.org/2000/svg">
                                <path d="M7.53854 12.9861C7.49301 11.7682 8.38204 10.7342 9.57335 10.4767C12.8492 9.7686 15.148 8.81422 17.6928 7.07013C19.3334 5.9457 21.5 5.9457 23.1406 7.07012C25.6853 8.81422 27.9841 9.7686 31.26 10.4767C32.4513 10.7342 33.3403 11.7682 33.2948 12.9861C32.8854 23.9386 29.2132 30.562 22.2783 34.7935C21.1357 35.4907 19.6976 35.4907 18.5551 34.7935C11.6201 30.562 7.94797 23.9386 7.53854 12.9861Z" fill="#121417" stroke="#121417" strokeWidth="1.5" strokeLinejoin="round"/>
                                <path fillRule="evenodd" clipRule="evenodd" d="M32.3183 10.9643C32.3971 11.2152 32.435 11.4825 32.4246 11.7593C32.0152 22.7118 28.3431 29.3352 21.4081 33.5667C20.2656 34.2639 18.8275 34.2639 17.6849 33.5667C15.2961 32.1091 13.2944 30.3678 11.6799 28.2634C9.16085 24.4154 7.7804 19.4561 7.53854 12.9861C7.49301 11.7682 8.38204 10.7342 9.57335 10.4767C12.8492 9.7686 15.148 8.81422 17.6928 7.07013C19.3334 5.9457 21.5 5.9457 23.1406 7.07012C25.6853 8.81422 27.9841 9.7686 31.26 10.4767C31.6533 10.5617 32.0136 10.7314 32.3183 10.9643Z" fill="#2767E7"/>
                                <path d="M32.3183 10.9643L33.0338 10.7396C32.9875 10.592 32.8967 10.4624 32.7738 10.3685L32.3183 10.9643ZM32.4246 11.7593L31.6752 11.7313V11.7313L32.4246 11.7593ZM21.4081 33.5667L21.7988 34.207L21.4081 33.5667ZM17.6849 33.5667L17.2943 34.207H17.2943L17.6849 33.5667ZM11.6799 28.2634L11.0524 28.6742C11.0626 28.6898 11.0735 28.7051 11.0848 28.7199L11.6799 28.2634ZM7.53854 12.9861L8.28801 12.9581L7.53854 12.9861ZM9.57335 10.4767L9.41489 9.74364H9.41489L9.57335 10.4767ZM17.6928 7.07013L17.2688 6.45148V6.45148L17.6928 7.07013ZM23.1406 7.07012L22.7166 7.68877L23.1406 7.07012ZM31.26 10.4767L31.1015 11.2098L31.26 10.4767ZM31.6027 11.189C31.6563 11.3597 31.6823 11.5416 31.6752 11.7313L33.1741 11.7873C33.1877 11.4234 33.1378 11.0706 33.0338 10.7396L31.6027 11.189ZM31.6752 11.7313C31.4733 17.1323 30.4686 21.4103 28.6971 24.8391C26.9301 28.2593 24.3794 30.8751 21.0175 32.9265L21.7988 34.207C25.3718 32.0268 28.1246 29.2152 30.0298 25.5276C31.9304 21.8488 32.9666 17.3388 33.1741 11.7873L31.6752 11.7313ZM21.0175 32.9265C20.1148 33.4773 18.9782 33.4773 18.0756 32.9265L17.2943 34.207C18.6767 35.0505 20.4164 35.0505 21.7988 34.207L21.0175 32.9265ZM18.0756 32.9265C15.7562 31.5113 13.8269 29.8297 12.2749 27.8068L11.0848 28.7199C12.762 30.9058 14.836 32.707 17.2943 34.207L18.0756 32.9265ZM12.3074 27.8526C9.88518 24.1526 8.52654 19.3388 8.28801 12.9581L6.78906 13.0142C7.03427 19.5735 8.43652 24.6783 11.0524 28.6742L12.3074 27.8526ZM8.28801 12.9581C8.25749 12.1415 8.85754 11.3988 9.73181 11.2098L9.41489 9.74364C7.90654 10.0697 6.72853 11.3948 6.78906 13.0142L8.28801 12.9581ZM9.73181 11.2098C13.0921 10.4834 15.4832 9.49372 18.1168 7.68877L17.2688 6.45148C14.8128 8.13472 12.6064 9.05378 9.41489 9.74364L9.73181 11.2098ZM18.1168 7.68877C19.5018 6.73949 21.3315 6.73949 22.7166 7.68877L23.5646 6.45148C21.6684 5.15191 19.1649 5.15192 17.2688 6.45148L18.1168 7.68877ZM22.7166 7.68877C25.3501 9.49372 27.7412 10.4834 31.1015 11.2098L31.4184 9.74364C28.227 9.05378 26.0205 8.13472 23.5646 6.45148L22.7166 7.68877ZM31.1015 11.2098C31.3859 11.2712 31.645 11.3936 31.8627 11.5601L32.7738 10.3685C32.3823 10.0691 31.9207 9.8522 31.4184 9.74364L31.1015 11.2098Z" fill="#121417"/>
                                <path d="M29.1666 35C31.9892 33.2099 35.031 29.1201 36.2754 24.2245" stroke="#121417" strokeWidth="1.5" strokeLinejoin="round"/>
                                <path d="M21.8677 21.2807C22.7435 20.7774 23.3333 19.8326 23.3333 18.75C23.3333 17.1392 22.0275 15.8333 20.4167 15.8333C18.8058 15.8333 17.5 17.1392 17.5 18.75C17.5 19.8326 18.0898 20.7774 18.9656 21.2807L18.465 24.8854C18.3954 25.3864 18.7846 25.8333 19.2904 25.8333H21.5429C22.0487 25.8333 22.4379 25.3864 22.3683 24.8854L21.8677 21.2807Z" fill="white"/>
                            </svg>
                        </div>
                        <div id="text-column">
                            <span id="title">{t('protect_account_security')}</span>
                            <p id="text">
                                {t('do_not_share_sensitive_info')}
                                {t('report_suspicious_messages')}
                            </p>
                            <div id="buttons">
                                <a href={CHAT_ISSUES_URL} target="_blank" rel="noreferrer">
                                    <span id="report-btn" className="report-button">
                                        {t('report_an_issue')}
                                    </span>
                                </a>
                                <a href="#readmore" onClick={() => onClickReadMore()} rel="noreferrer">
                                    <span id="read-btn" className="read-button">
                                        {t('read_more')}
                                    </span>
                                </a>
                            </div>
                        </div>
                    </div>
                </div>
            <GroupChannel
                channelUrl={channelUrl}
                className="sendbird-snippet-react"
                renderChannelHeader={() => <></>}
                renderMessageInput={getMessageInputRenderer(chatStatus)}
                filterEmojiCategoryIds={() => {
                    return [Number.parseInt(process.env.REACT_APP_INBOX_EMOJIS_GROUP_ID, 10)];
                }}
                onBeforeSendUserMessage={handleAfterSendUserMessage}
            />
        </div>
    );
}

CustomGroupChannel.propTypes = {
    channelUrl: PropTypes.string.isRequired,
    userNickname: PropTypes.string.isRequired,
    chatStatus: PropTypes.shape({
        status: PropTypes.number,
        message: PropTypes.string,
    }).isRequired,
    showHeader: PropTypes.bool.isRequired,
    chatBlockedByModeration: PropTypes.bool,
};

export default CustomGroupChannel;